import { read, TABLE } from '../data/db';
import {
  annotations,
  coachCards,
  flagResolutions,
  hypotheses,
  matchNotes,
  matches,
  observations,
  setPieces,
  teams,
} from '../data/repos';
import { finalMinute } from '../domain/clock';
import { compareConfirmedFirst } from '../domain/order';
import type {
  AnnotationRow,
  CoachCardRow,
  HypothesisRow,
  MatchNoteRow,
  MatchRow,
  SetPieceRow,
} from '../types';
import { dispatch } from './bus';
import type { DispatchResult } from './bus';
import { cardSourcesFrom } from './coach';
import type { CoachCardSource } from './coach';
import { firstIncrements } from './discovery';
import { StorageError } from './errors';

/**
 * B spec §2.4 / §4.5 — **the match record's read and its one lifecycle move** (build unit B-18).
 *
 * ── What this facade is, and what it deliberately is not ─────────────────────
 *
 * The record is the post-match screen: what the match found, what is still unfinished, and the two
 * things the user can still do to it (build the full-time card, complete the match). It is a
 * reading surface reached out-of-match, so a wide read is the honest way to compose it — every
 * table in the scope below is rendered on the screen.
 *
 * **It is not `loadTriageContext` widened, and it does not re-derive what that read already gives.**
 * A's triage read owns the boundary-adjustment half — the match, the opponent, and the rederivable
 * Observation/MatchNote rows `previewAdjustment` needs — and B's record calls it **as-is**, lazily,
 * when the Adjust-clock pane opens (`app/triage.ts`'s own `countUnresolvedFlags` argument: a screen's
 * cost does not belong on a control nobody may tap). What is here is only what A has no read for:
 * hypothesis outcomes with their annotations, the flag ledger including resolved flags, set pieces,
 * notes, and the coach cards with the patterns they cite. Two reads on one post-match screen is A's
 * own `TriageScreen` shape (`loadTriageContext` + `loadFlagsContext`), not a new pattern.
 *
 * ── One transaction, because the completion guard depends on the agreement ───
 *
 * The unresolved-flag count and the draft-card list are the two facts F-1 refuses on. Read at two
 * instants they could describe different moments, and the screen would then offer *Complete match*
 * against a state it is no longer showing. They are read here together, through the **same**
 * `listUnresolvedFlags` the engine's own guard reads — shared read, not a cloned rule.
 */

const MODULE = 'app/matchRecord';

/** One pattern as the record reports it: the row, and everything the user wrote about it. */
export interface RecordOutcome {
  readonly hypothesis: HypothesisRow;
  /** Oldest first — `annotations.listForMatch`'s order, the one the detail sheet shows. */
  readonly annotations: readonly AnnotationRow[];
}

/**
 * One flag of the match, **resolved ones included** — scenario 33 asks for a past match's flags and
 * voice notes to be reviewable, and the triage queue by construction holds only the unresolved
 * ones. After F-1's guard is satisfied that queue is empty, so a record that showed only the queue
 * would show a completed match no flags at all.
 */
export interface RecordFlag {
  readonly observationId: string;
  readonly matchMinute: number;
  /** `null` — a silent flag (FLAG-4's mic-less path). Non-null renders playback. */
  readonly audioBlobId: string | null;
  /** Whether triage has already dismissed / attached / promoted it. */
  readonly resolved: boolean;
}

/** Everything the match record renders, from one readonly transaction. */
export interface MatchRecordView {
  readonly match: MatchRow;
  readonly opponent: string;
  /** §2.4's header minute — `domain/clock.finalMinute`, the single temporal authority. */
  readonly finalMinute: number;
  /**
   * Every hypothesis of the match in §2.4's stated order (`compareConfirmedFirst`: confirmed first,
   * then count desc, then §6.1's total tiebreak). **Unfiltered**: the observed/never-seen split is a
   * rendering decision (`ui-b/record/recordB.ts`), and a pattern deliberately marked *invalidated*
   * with no observations is an outcome even though its count is zero.
   */
  readonly outcomes: readonly RecordOutcome[];
  /** Recorded instances across the whole match — the ledger's `increment` rows. */
  readonly incrementCount: number;
  /** Minute-ascending, the order the match happened in. */
  readonly flags: readonly RecordFlag[];
  /** DIST-1's derived count, from the repo function the engine's own guards read. */
  readonly unresolvedFlagCount: number;
  readonly notes: readonly MatchNoteRow[];
  readonly setPieces: readonly SetPieceRow[];
  /** Halftime card first (the repo's order), then full time. Drafts included — the queue chases them. */
  readonly cards: readonly CoachCardRow[];
  /** Every pattern this match's cards cite, resolved once for both of them. */
  readonly cardSources: readonly CoachCardSource[];
}

/**
 * §2.4 — the whole record, one readonly transaction.
 *
 * @throws {StorageError} on a read failure, and on a match that does not exist. Not swallowed: an
 *   empty record and an unreadable one look identical on screen, and only one of them is a state
 *   the user can do anything about (the `loadTriageContext` rule).
 */
export function loadMatchRecord(matchId: string): Promise<MatchRecordView> {
  return read(
    [
      TABLE.matches,
      TABLE.teams,
      TABLE.hypotheses,
      TABLE.observations,
      TABLE.flagResolutions,
      TABLE.annotations,
      TABLE.matchNotes,
      TABLE.setPieces,
      TABLE.coachCards,
    ],
    async (): Promise<MatchRecordView> => {
      const match = await matches.getMatch(matchId);
      if (match === undefined) {
        throw new StorageError('That match no longer exists.', {
          context: { store: TABLE.matches, matchId },
        });
      }

      const [rows, ledger, unresolved, notes, cards, pieces, allAnnotations] = await Promise.all([
        hypotheses.listHypothesesForMatch(matchId),
        observations.listObservationsForMatch(matchId),
        flagResolutions.listUnresolvedFlags(matchId),
        matchNotes.listForMatch(matchId),
        coachCards.listForMatch(matchId),
        setPieces.listForMatch(matchId),
        annotations.listForMatch(matchId),
      ]);

      // §6.1's second sort term, from the helper every ranked load uses (`loadDiscovery`,
      // `loadHalftime`, `loadWorkflowContext`): one derivation, so this list and the workflow's
      // can never disagree about a rank.
      const firstIncrementByHypothesis = firstIncrements(ledger);
      const notesByHypothesis = new Map<string, AnnotationRow[]>();
      for (const note of allAnnotations) {
        const bucket = notesByHypothesis.get(note.hypothesisId);
        if (bucket === undefined) notesByHypothesis.set(note.hypothesisId, [note]);
        else bucket.push(note);
      }

      const unresolvedIds = new Set(unresolved.map((row) => row.id));
      const cited = new Set(
        cards.flatMap((card) => card.adjustments.flatMap((entry) => entry.sourceHypothesisIds)),
      );

      return {
        match,
        // `loadHomeB`'s fallback, for its reason: a cascade cannot orphan a match (INV-8), so a
        // missing team means a partially restored archive — and a record that refused to render
        // over it would hide the very data the user is trying to recover.
        opponent: (await teams.getTeam(match.teamId))?.name ?? 'Opponent',
        finalMinute: finalMinute(match),
        outcomes: rows
          .map((hypothesis) => ({
            hypothesis,
            key: {
              id: hypothesis.id,
              cachedCount: hypothesis.cachedCount,
              firstObservationCreatedAt: firstIncrementByHypothesis.get(hypothesis.id) ?? null,
              createdAt: hypothesis.createdAt,
              status: hypothesis.status,
            },
          }))
          .sort((a, b) => compareConfirmedFirst(a.key, b.key))
          .map(({ hypothesis }) => ({
            hypothesis,
            annotations: notesByHypothesis.get(hypothesis.id) ?? [],
          })),
        incrementCount: ledger.filter((row) => row.kind === 'increment').length,
        flags: ledger
          .filter((row) => row.kind === 'flag')
          .sort((a, b) => a.matchMinute - b.matchMinute || a.createdAt - b.createdAt)
          .map((row) => ({
            observationId: row.id,
            matchMinute: row.matchMinute,
            audioBlobId: row.audioBlobId,
            resolved: !unresolvedIds.has(row.id),
          })),
        unresolvedFlagCount: unresolved.length,
        notes,
        setPieces: pieces,
        cards,
        cardSources: cardSourcesFrom(rows, allAnnotations, cited),
      };
    },
    MODULE,
  );
}

/**
 * B §6 / F-1 — **complete the match**: `fullTime → archived`.
 *
 * Both guards belong to `machine.transition`, read from the snapshot its transaction opened with:
 * zero unresolved flags (`ERR_UNRESOLVED_FLAGS`, the same rule `PassTriage` enforces) and zero
 * draft cards (`ERR_CARD_DRAFTS`). The record disables the control while it can see either
 * blocker, and the two are not redundant — the disable is what the user reads, the guard is what
 * makes it true when a draft was committed by another surface a moment ago (`app/triage.ts`'s
 * `passTriage` argument, one lifecycle step over).
 */
export function completeMatch(matchId: string): Promise<DispatchResult> {
  return dispatch({ type: 'Transition', event: 'CompleteMatch', matchId });
}
